import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import type { Company } from "newui/features/Company/types";
import type {
	ScoreRange,
	ScoringInstructionsState,
	ScoringInstructionRow,
} from "../../types/ScoringInstruction.type";
import { validateScoringInstructions } from "../../utils/scoringValidation";
import { useScoringInstructions } from "../../hooks/useScoringInstructions";
import { ScoringInstructionsTable } from "./ScoringInstructionsTable";
import { ScoringInstructionsToolbar } from "./ScoringInstructionsToolbar";

interface ScoringInstructionsProps {
	company: Company;
}

const generateId = (): string => {
	return `range-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

/**
 * Map API ScoringInstructionRow[] to local ScoreRange[] for editing.
 */
const apiRowsToRanges = (rows: ScoringInstructionRow[]): ScoreRange[] => {
	return rows
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map((row) => ({
			id: String(row.id),
			min: row.minScore,
			max: row.maxScore,
			isSingleValue: row.minScore === row.maxScore,
			description: row.description,
		}));
};

/**
 * Map local ScoreRange[] to API payload rows.
 */
const rangesToApiRows = (ranges: ScoreRange[]) => {
	return ranges.map((range, index) => ({
		min_score: range.min,
		max_score: range.max,
		description: range.description,
		sort_order: index,
	}));
};

export const ScoringInstructions: React.FC<ScoringInstructionsProps> = ({ company }) => {
	const {
		instructions,
		isLoading,
		isSaving,
		error,
		fetchInstructions,
		addInstruction,
		editInstruction,
		handleExport,
		handleDownloadTemplate,
		handleImport,
	} = useScoringInstructions();

	const [state, setState] = useState<ScoringInstructionsState>({
		totalScore: 10,
		ranges: [],
	});

	// Track the active instruction UUID for updates vs creates
	const activeInstructionUuid = useRef<string | null>(null);

	// Load on mount
	const hasFetched = useRef(false);
	useEffect(() => {
		if (!hasFetched.current) {
			hasFetched.current = true;
			fetchInstructions();
		}
	}, [fetchInstructions]);

	// Sync API data into local editing state
	useEffect(() => {
		if (instructions.length > 0) {
			const instruction = instructions[0];
			activeInstructionUuid.current = instruction.uuid;
			setState({
				totalScore: instruction.totalScore,
				ranges: apiRowsToRanges(instruction.rows),
			});
		}
	}, [instructions]);

	const validation = useMemo(() => validateScoringInstructions(state), [state]);

	const handleTotalScoreChange = useCallback((value: number) => {
		setState((prev) => ({ ...prev, totalScore: value }));
	}, []);

	const handleUpdateRange = useCallback((id: string, updates: Partial<ScoreRange>) => {
		setState((prev) => ({
			...prev,
			ranges: prev.ranges.map((r) => (r.id === id ? { ...r, ...updates } : r)),
		}));
	}, []);

	const handleDeleteRange = useCallback((id: string) => {
		setState((prev) => ({
			...prev,
			ranges: prev.ranges.filter((r) => r.id !== id),
		}));
	}, []);

	const handleMoveRange = useCallback((id: string, direction: "up" | "down") => {
		setState((prev) => {
			const idx = prev.ranges.findIndex((r) => r.id === id);
			if (idx === -1) {
				return prev;
			}

			const newIdx = direction === "up" ? idx - 1 : idx + 1;
			if (newIdx < 0 || newIdx >= prev.ranges.length) {
				return prev;
			}

			const newRanges = [...prev.ranges];
			[newRanges[idx], newRanges[newIdx]] = [newRanges[newIdx], newRanges[idx]];
			return { ...prev, ranges: newRanges };
		});
	}, []);

	const handleToggleRangeType = useCallback((id: string) => {
		setState((prev) => ({
			...prev,
			ranges: prev.ranges.map((r) => {
				if (r.id !== id) {
					return r;
				}
				const isSingleValue = !r.isSingleValue;
				return {
					...r,
					isSingleValue,
					max: isSingleValue ? r.min : r.max,
				};
			}),
		}));
	}, []);

	const handleAddRow = useCallback(() => {
		const firstRange = state.ranges[0];
		const newMax = firstRange ? Math.max(firstRange.max + 1, state.totalScore) : state.totalScore;
		const newMin = Math.max(newMax - 1, 0);

		const newRange: ScoreRange = {
			id: generateId(),
			min: newMin,
			max: newMax,
			isSingleValue: false,
			description: "",
		};

		setState((prev) => ({
			...prev,
			ranges: [newRange, ...prev.ranges],
		}));
	}, [state.ranges, state.totalScore]);

	const handleSave = useCallback(async () => {
		if (!validation.isValid) {
			return;
		}

		const payload = {
			name: "Default Scoring Instructions",
			total_score: state.totalScore,
			rows: rangesToApiRows(state.ranges),
		};

		if (activeInstructionUuid.current) {
			await editInstruction(activeInstructionUuid.current, payload);
		} else {
			const created = await addInstruction(payload);
			if (created) {
				activeInstructionUuid.current = created.uuid;
			}
		}
	}, [validation.isValid, state, editInstruction, addInstruction]);

	const onImport = useCallback(() => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = ".xlsx,.xls,.csv";
		input.onchange = async (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				await handleImport(file);
			}
		};
		input.click();
	}, [handleImport]);

	const onExport = useCallback(() => {
		handleExport();
	}, [handleExport]);

	const onDownloadTemplate = useCallback(() => {
		handleDownloadTemplate();
	}, [handleDownloadTemplate]);

	if (isLoading) {
		return (
			<div className="p-6" data-testid="scoring-instructions-loading">
				<p className="font-roboto text-sm text-gray-500">Loading scoring instructions...</p>
			</div>
		);
	}

	return (
		<div className="p-6" data-testid="scoring-instructions">
			{error && (
				<div className="mb-4 rounded border border-red-200 bg-red-50 p-3">
					<p className="font-roboto text-sm text-red-700">{error}</p>
				</div>
			)}

			<ScoringInstructionsToolbar
				totalScore={state.totalScore}
				onTotalScoreChange={handleTotalScoreChange}
				onImport={onImport}
				onExport={onExport}
				onDownloadTemplate={onDownloadTemplate}
				onAddRow={handleAddRow}
				isSaving={isSaving}
				isValid={validation.isValid}
				onSave={handleSave}
			/>

			{validation.warnings.length > 0 && (
				<div className="mb-4 rounded border border-yellow-200 bg-yellow-50 p-3">
					{validation.warnings.map((warning, idx) => (
						<p key={idx} className="font-roboto text-sm text-yellow-700">
							{warning}
						</p>
					))}
				</div>
			)}

			<ScoringInstructionsTable
				ranges={state.ranges}
				totalScore={state.totalScore}
				validation={validation}
				onUpdateRange={handleUpdateRange}
				onDeleteRange={handleDeleteRange}
				onMoveRange={handleMoveRange}
				onToggleRangeType={handleToggleRangeType}
			/>
		</div>
	);
};
